import React from 'react';
import { HANDBUCH_FARBEN } from './stil';
import type { FristLage } from '../handbuchLogic';
import type { NachweisStatus, RisikoStufe } from '../types';

/**
 * Die Statuszeichen des gedruckten Handbuchs.
 *
 * Portiert aus `components/shared/StatusBadge.tsx` der Quell-App, mit einer
 * fachlichen Berichtigung: dort gab es ZWEI Statuslisten, `DokumentStatus`
 * (vorhanden/fehlt/pruefung) und `NachweisStatus` (vorhanden/fehlt/faellig) —
 * und beide bezeichneten dasselbe Ding. „faellig" ist aber gar kein Status,
 * sondern eine Aussage ueber die FRIST. Ein Nachweis kann fehlen UND
 * ueberfaellig sein; wer das in einen Wert presst, verliert eine der beiden
 * Aussagen. Deshalb hier zwei getrennte Zeichen: `NachweisStatusZeichen`
 * (was ist da?) und `FristZeichen` (wie steht die Frist?).
 *
 * Die Farben kommen aus `HANDBUCH_FARBEN` und nicht aus globalen CSS-Klassen
 * (`.status-ok`, `.risk-high` in der Quell-App) — Begruendung in `stil.ts`.
 *
 * ═══ WARUM NEBEN DER FARBE IMMER EIN ZEICHEN STEHT ═════════════════════════
 * Rot und Gruen unterscheiden sich fuer einen Teil der Leser nicht, und das
 * Handbuch wird ausgedruckt — mitunter auf einem Schwarz-Weiss-Geraet im
 * Baucontainer. Ein Zustand, den nur die Farbe traegt, ist auf diesem Ausdruck
 * verschwunden.
 */

const zeichenStil = (farbe: string): React.CSSProperties => ({
    color: farbe,
    fontWeight: 700,
    whiteSpace: 'nowrap',
});

/** Was liegt vor? */
export const NachweisStatusZeichen: React.FC<{
    status: NachweisStatus;
    /** Mit Beschriftung statt nur mit Symbol — fuer Listen, in denen das
     *  Zeichen ohne Spaltenkopf steht. */
    mitText?: boolean;
}> = ({ status, mitText = false }) => {
    const nach: Record<NachweisStatus, { zeichen: string; text: string; farbe: string }> = {
        vorhanden: { zeichen: '✓', text: 'vorhanden', farbe: HANDBUCH_FARBEN.statusOk },
        pruefung: { zeichen: '○', text: 'in Prüfung', farbe: HANDBUCH_FARBEN.statusOffen },
        fehlt: { zeichen: '✗', text: 'fehlt', farbe: HANDBUCH_FARBEN.statusFehlt },
    };
    const a = nach[status] ?? nach.fehlt;
    return <span style={zeichenStil(a.farbe)}>{a.zeichen}{mitText ? ` ${a.text}` : ''}</span>;
};

/**
 * Wie steht die Frist?
 *
 * `erledigt` und `ohne_frist` geben ABSICHTLICH nichts aus. Ein Zeichen fuer
 * „keine Frist gesetzt" saehe in der Spalte aus wie ein Mangel, und ein
 * erledigter Nachweis braucht keine Fristaussage mehr.
 */
export const FristZeichen: React.FC<{ lage: FristLage }> = ({ lage }) => {
    if (lage === 'ueberfaellig') {
        return <span style={zeichenStil(HANDBUCH_FARBEN.statusFehlt)}>! überfällig</span>;
    }
    if (lage === 'bald') {
        return <span style={zeichenStil(HANDBUCH_FARBEN.statusOffen)}>△ Frist läuft</span>;
    }
    return null;
};

/** Risikostufe einer Eigengefaehrdung. */
export const RisikoZeichen: React.FC<{ risiko: RisikoStufe; mitText?: boolean }> = ({
    risiko, mitText = false,
}) => {
    const nach: Record<RisikoStufe, { zeichen: string; text: string; farbe: string }> = {
        hoch: { zeichen: '●●●', text: 'hoch', farbe: HANDBUCH_FARBEN.risikoHoch },
        mittel: { zeichen: '●●', text: 'mittel', farbe: HANDBUCH_FARBEN.risikoMittel },
        niedrig: { zeichen: '●', text: 'niedrig', farbe: HANDBUCH_FARBEN.risikoNiedrig },
    };
    const a = nach[risiko] ?? nach.mittel;
    return <span style={zeichenStil(a.farbe)}>{a.zeichen}{mitText ? ` ${a.text}` : ''}</span>;
};

/**
 * Vollstaendigkeit eines Kapitels in der Uebersicht am Bildschirm.
 *
 * Bewusst hier und nicht in `HandbuchModul.tsx`: es ist dieselbe Aussage wie
 * im gedruckten Verzeichnis und soll auch gleich aussehen.
 */
export const KapitelZeichen: React.FC<{ vollstaendig: boolean; pflicht: boolean }> = ({
    vollstaendig, pflicht,
}) => {
    if (vollstaendig) {
        return <span style={zeichenStil(HANDBUCH_FARBEN.statusOk)}>✓</span>;
    }
    return (
        <span style={zeichenStil(pflicht ? HANDBUCH_FARBEN.statusFehlt : HANDBUCH_FARBEN.statusOffen)}>
            {pflicht ? '✗' : '○'}
        </span>
    );
};
